import {
  type CatalogCategory,
  type CatalogItem,
  type PriceListRow,
  type PricingMethod,
  type Prices,
  emptyPrices,
  hasPrice,
  unitTotal,
} from "./labCatalog";
import { priceListParams } from "./useLabCatalog";
import type { LabOrderDetail } from "./labOrderTypes";

export { priceListParams };

/** A lab's price list row (kept under the old name for the order screens). */
export type LabPriceListItem = PriceListRow;

/**
 * One order line. It names a master catalogue item through the lab's price
 * list row (registered lab: labItemId, priced by the lab; hand-added lab:
 * localItemId, priced by the clinic) or, for a hand-added lab, a catalogue
 * item not on its list yet (added to the list when the order is saved).
 */
export type InvoiceLineForm = {
  key: string;
  labItemId?: string;
  localItemId?: string;
  catalogItemId: string;
  /** Name shown: the lab's own name, else the catalogue name */
  item: string;
  catalogName: string;
  categoryId: string | null;
  categoryName: string;
  caseCode: number | null;
  pricingMethod: PricingMethod;
  quantity: number;
  /** Base + per tooth lines */
  teeth: number | null;
  /** Teeth follow the tooth chart until someone types a number */
  teethAuto: boolean;
  prices: Prices;
  /** A registered lab prices the line; the clinic can't change it */
  priceLocked: boolean;
  notes: string;
  lineDiscount: number;
};

export type LabOrderFormValues = {
  labId: string;
  labPartnerId: string;
  caseTitle: string;
  caseMeta: string;
  caseDetail: string;
  /** FDI tooth numbers or mode tokens: full | upper | lower */
  toothNos: string[];
  patient: string;
  patientMeta: string;
  orderDate: string;
  dueDate: string;
  clinicLocation: string;
  receivedBy: string;
  receivedType: string;
  deliveryAddress: string;
  contact: string;
  invoiceItems: InvoiceLineForm[];
  generalDiscount: number;
  deliveryCharge: number;
  priority: "Normal" | "Urgent";
  shade: string;
  shadeMix: string;
  /** "" = the order's own lab */
  modelledByPartnerId: string;
  dentistId: string;
  /** "" = the first line's category */
  caseCategoryId: string;
};

let keySeq = 0;
const nextKey = () => `line-${++keySeq}`;

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

export function clinicSenderDefaults(clinic: {
  name: string;
  phone?: string;
  address?: string;
}): Pick<LabOrderFormValues, "receivedBy" | "receivedType" | "clinicLocation" | "deliveryAddress" | "contact"> {
  const address = clinic.address?.trim() || "";
  const phone = clinic.phone?.trim() || "";
  return {
    receivedBy: clinic.name.trim() || "Clinic",
    receivedType: "Delivery",
    clinicLocation: address,
    deliveryAddress: address,
    contact: phone,
  };
}

export function emptyLabOrderValues(): LabOrderFormValues {
  return {
    labId: "",
    labPartnerId: "",
    caseTitle: "",
    caseMeta: "",
    caseDetail: "",
    toothNos: [],
    patient: "",
    patientMeta: "",
    orderDate: todayIsoDate(),
    dueDate: "",
    clinicLocation: "",
    receivedBy: "Clinic",
    receivedType: "Delivery",
    deliveryAddress: "",
    contact: "",
    invoiceItems: [],
    generalDiscount: 0,
    deliveryCharge: 0,
    priority: "Normal",
    shade: "",
    shadeMix: "",
    modelledByPartnerId: "",
    dentistId: "",
    caseCategoryId: "",
  };
}

/** A line from the lab's price list. */
export function lineFromRow(row: PriceListRow, { registered, chartTeeth }: { registered: boolean; chartTeeth: number }): InvoiceLineForm {
  const tooth = row.pricingMethod === "base_plus_tooth";
  return {
    key: nextKey(),
    labItemId: registered ? row.labItemId || row.id : undefined,
    localItemId: registered ? undefined : row.localItemId || row.id,
    catalogItemId: row.catalogItemId || "",
    item: row.name,
    catalogName: row.catalogName || row.name,
    categoryId: row.categoryId,
    categoryName: row.categoryName,
    caseCode: row.caseCode,
    pricingMethod: row.pricingMethod,
    quantity: 1,
    teeth: tooth ? chartTeeth || null : null,
    teethAuto: tooth,
    prices: { ...emptyPrices(), ...row.prices },
    priceLocked: registered,
    notes: "",
    lineDiscount: 0,
  };
}

/** A hand-added lab: a catalogue item not on its list yet (joins the list on save). */
export function lineFromCatalog(item: CatalogItem, category: CatalogCategory, chartTeeth: number): InvoiceLineForm {
  const tooth = item.pricingMethod === "base_plus_tooth";
  return {
    key: nextKey(),
    catalogItemId: item.id,
    item: item.name,
    catalogName: item.name,
    categoryId: category.id,
    categoryName: category.name,
    caseCode: category.caseCode,
    pricingMethod: item.pricingMethod,
    quantity: 1,
    teeth: tooth ? chartTeeth || null : null,
    teethAuto: tooth,
    prices: { ...emptyPrices(), includedTeeth: 0 },
    priceLocked: false,
    notes: "",
    lineDiscount: 0,
  };
}

const lineKey = (l: InvoiceLineForm) =>
  [l.labItemId || l.localItemId || `cat:${l.catalogItemId}`, l.teeth ?? "", l.priceLocked ? "" : JSON.stringify(l.prices)].join("|");

/** Adds the line, or raises the quantity of the same item at the same price. */
export function addLine(lines: InvoiceLineForm[], line: InvoiceLineForm): InvoiceLineForm[] {
  const at = lines.findIndex((l) => lineKey(l) === lineKey(line));
  if (at === -1) return [...lines, line];
  return lines.map((l, i) => (i === at ? { ...l, quantity: Math.min(99, l.quantity + 1) } : l));
}

/** Lines whose teeth follow the chart take the chart's count. */
export function applyChartTeeth(lines: InvoiceLineForm[], count: number): InvoiceLineForm[] {
  let changed = false;
  const next = lines.map((l) => {
    if (!l.teethAuto || l.pricingMethod !== "base_plus_tooth") return l;
    const teeth = count || null;
    if (teeth === l.teeth) return l;
    changed = true;
    return { ...l, teeth };
  });
  return changed ? next : lines;
}

export function lineUnit(line: InvoiceLineForm) {
  return unitTotal(line.pricingMethod, line.prices, line.teeth);
}

export function lineNetPrice(line: InvoiceLineForm) {
  return Math.max(0, lineUnit(line) * line.quantity - (Number(line.lineDiscount) || 0));
}

/** What the server needs per line; a registered lab's line sends no price. */
export function linePayload(line: InvoiceLineForm) {
  const base = {
    labItemId: line.labItemId || undefined,
    localItemId: line.localItemId || undefined,
    catalogItemId: line.labItemId || line.localItemId ? undefined : line.catalogItemId || undefined,
    item: line.item,
    quantity: line.quantity,
    notes: line.notes.trim(),
    lineDiscount: Number(line.lineDiscount) || 0,
    ...(line.pricingMethod === "base_plus_tooth" ? { teeth: line.teeth ?? undefined } : {}),
  };
  if (line.priceLocked) return base;
  const p = line.prices;
  return line.pricingMethod === "base_plus_tooth"
    ? { ...base, basePrice: p.basePrice, toothPrice: p.toothPrice, includedTeeth: p.includedTeeth ?? 0 }
    : { ...base, price: p.price };
}

export function computeOrderTotals(values: Pick<LabOrderFormValues, "invoiceItems" | "generalDiscount" | "deliveryCharge">) {
  const invoiceItems = values.invoiceItems.map(linePayload);
  const netTotal = values.invoiceItems.reduce((sum, l) => sum + lineNetPrice(l), 0);
  const generalDiscount = Number(values.generalDiscount) || 0;
  const deliveryCharge = Number(values.deliveryCharge) || 0;
  const finalTotal = Math.max(0, netTotal - generalDiscount + deliveryCharge);
  return { invoiceItems, netTotal, generalDiscount, deliveryCharge, finalTotal };
}

/** Why the lines can't be saved yet, or "". Hand-added labs may leave the price for later. */
export function orderLinesError(lines: InvoiceLineForm[]) {
  if (!lines.length) return "Add at least one lab item";
  for (const l of lines) {
    if (l.pricingMethod === "base_plus_tooth" && !(Number(l.teeth) >= 1)) {
      return `Enter the number of teeth for ${l.item}`;
    }
  }
  return "";
}

/** True when a hand-added lab's line still has no price (allowed; shown as "price later"). */
export const linePriceMissing = (l: InvoiceLineForm) => !l.priceLocked && !hasPrice(l.pricingMethod, l.prices);

export function buildClinicOrderPayload(values: LabOrderFormValues, options?: { treatmentId?: string }) {
  const { invoiceItems, netTotal, generalDiscount, deliveryCharge, finalTotal } = computeOrderTotals(values);
  const caseTitle = values.caseTitle.trim();
  return {
    labId: values.labId || undefined,
    labPartnerId: values.labPartnerId,
    case: caseTitle,
    caseTitle,
    caseMeta: values.caseMeta.trim() || undefined,
    caseDetail: values.caseDetail.trim() || caseTitle,
    toothNos: values.toothNos.map((t) => String(t).trim()).filter(Boolean),
    patient: values.patient.trim(),
    patientMeta: values.patientMeta.trim() || undefined,
    // YYYY-MM-DD; the server stores dates in that form
    orderDate: values.orderDate || undefined,
    dueDate: values.dueDate || undefined,
    clinicLocation: values.clinicLocation.trim() || undefined,
    receivedBy: values.receivedBy.trim() || "Clinic",
    receivedType: values.receivedType.trim() || "Delivery",
    deliveryAddress: values.deliveryAddress.trim() || undefined,
    contact: values.contact.trim() || undefined,
    addedBy: "Clinic",
    invoiceItems,
    netTotal,
    generalDiscount,
    deliveryCharge,
    finalTotal,
    orderStatus: "Pending",
    payment: "Unpaid",
    treatmentId: options?.treatmentId?.trim() || undefined,
    priority: values.priority,
    shade: values.shade.trim() || undefined,
    shadeMix: values.shadeMix.trim() || undefined,
    modelledByPartnerId: values.modelledByPartnerId || undefined,
    dentistId: values.dentistId || undefined,
    caseCategoryId: values.caseCategoryId || undefined,
  };
}

/** Saved order lines back into form lines (editing an order). */
export function linesFromOrder(
  saved: Array<Record<string, unknown>>,
  { registered }: { registered: boolean }
): InvoiceLineForm[] {
  return (saved || []).map((l) => {
    const method = (l.pricingMethod as PricingMethod) || "flat";
    const num = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));
    return {
      key: nextKey(),
      labItemId: (l.labItemId as string) || undefined,
      localItemId: (l.localItemId as string) || undefined,
      catalogItemId: (l.catalogItemId as string) || "",
      item: String(l.item || ""),
      catalogName: String(l.catalogName || l.item || ""),
      categoryId: (l.categoryId as string) || null,
      categoryName: String(l.categoryName || ""),
      caseCode: num(l.caseCode),
      pricingMethod: method,
      quantity: Number(l.quantity) || 1,
      teeth: num(l.teeth),
      teethAuto: false,
      prices:
        method === "base_plus_tooth"
          ? { price: null, basePrice: num(l.basePrice), toothPrice: num(l.toothPrice), includedTeeth: num(l.includedTeeth) }
          : { ...emptyPrices(), price: num(l.unitPrice ?? l.price) },
      priceLocked: registered,
      notes: String(l.notes || ""),
      lineDiscount: Number(l.lineDiscount) || 0,
    };
  });
}

/** An order's detail as form values, for the edit form. */
export function valuesFromOrder(order: LabOrderDetail): LabOrderFormValues {
  const registered = !(order.isLocalTracking || order.trackingMode === "local");
  return {
    labId: order.labId || "",
    labPartnerId: order.labPartnerId || "",
    caseTitle: order.caseTitle || order.case || "",
    caseMeta: order.caseMeta || "",
    caseDetail: order.caseDetail && order.caseDetail !== order.caseTitle ? order.caseDetail : "",
    toothNos: Array.isArray(order.toothNos) ? order.toothNos : [],
    patient: order.patient || "",
    patientMeta: order.patientMeta || "",
    orderDate: order.orderDateIso || todayIsoDate(),
    dueDate: /^\d{4}-\d{2}-\d{2}$/.test(order.dueDate || "") ? (order.dueDate as string) : "",
    clinicLocation: order.clinicLocation && order.clinicLocation !== "—" ? order.clinicLocation : "",
    receivedBy: order.receivedBy || "Clinic",
    receivedType: order.receivedType || "Delivery",
    deliveryAddress: order.deliveryAddress && order.deliveryAddress !== "—" ? order.deliveryAddress : "",
    contact: order.contact && order.contact !== "—" ? order.contact : "",
    invoiceItems: linesFromOrder(order.invoiceItems as unknown as Array<Record<string, unknown>>, { registered }),
    generalDiscount: order.generalDiscount || 0,
    deliveryCharge: order.deliveryCharge || 0,
    priority: order.priority === "Urgent" ? "Urgent" : "Normal",
    shade: order.shade || "",
    shadeMix: order.shadeMix || "",
    modelledByPartnerId:
      order.modelledByPartnerId && order.modelledByPartnerId !== order.labPartnerId ? order.modelledByPartnerId : "",
    dentistId: order.dentistId || "",
    caseCategoryId: order.caseCategoryId || "",
  };
}

/** The PATCH body for an edit: only the fields the server lets the clinic change now. */
export function editPayload(values: LabOrderFormValues, editableFields: string[]) {
  const can = new Set(editableFields);
  const full = buildClinicOrderPayload(values);
  const body: Record<string, unknown> = {};
  const take = (field: string, value: unknown) => {
    if (can.has(field)) body[field] = value;
  };
  take("caseTitle", full.caseTitle);
  take("caseMeta", values.caseMeta.trim());
  take("caseDetail", values.caseDetail.trim());
  take("toothNos", full.toothNos);
  take("dueDate", values.dueDate);
  take("priority", values.priority);
  take("invoiceItems", full.invoiceItems);
  take("caseCategoryId", values.caseCategoryId || undefined);
  take("deliveryAddress", values.deliveryAddress.trim());
  take("contact", values.contact.trim());
  take("modelledByPartnerId", values.modelledByPartnerId || values.labPartnerId);
  take("dentistId", values.dentistId);
  take("shade", values.shade.trim());
  take("shadeMix", values.shadeMix.trim());
  return body;
}
